<template>
  <!-- id=#app is used for e2e test -->
  <span
    id="app"
    class="hf-stats"
  >
    <NavigationHeader/>
    <div
      class="hf-content"
      :class="{'hf-content-no-nav-bar': nonavbar, 'hf-content-wide': route.meta.wide === true}"
    >
      <router-view v-slot="{Component}">
        <keep-alive :max="100">
          <component
            :is="Component"
            :key="route.fullPath"
          />
        </keep-alive>
      </router-view>
    </div>
  </span>
</template>

<script setup lang="ts">
  import {computed, onBeforeMount, onBeforeUpdate, onMounted, onUnmounted} from 'vue';
  import {useRoute, useRouter} from 'vue-router';
  import NavigationHeader from '@/components/layout/NavigationHeader.vue';
  import configuration from '@/assets/static/configuration.json';
  import {SiteConfiguration} from '@/model/site/context.model';
  import {HealthService} from '@/services/health.service';
  import {sendSize, useContextStore} from '@/store/context.store';
  import {log} from '@/utils/logger';

  const SERVER_ERROR_PAGE = '/503';
  const DEFAULT_HEALTH_TIMEOUT_MS = 1000;

  const route = useRoute();
  const router = useRouter();
  const context = useContextStore();
  const healthService = new HealthService();

  const siteConfiguration: SiteConfiguration = configuration;
  const siteConfig = siteConfiguration.site;
  if (siteConfig && siteConfig.developerMode && import.meta.env.PROD) {
    siteConfig.developerMode = false;
  }
  log.debug('Using configuration', siteConfiguration);
  context.setConfiguration(siteConfiguration);

  const nonavbar = computed(() => route.query.nonavbar === '1');

  function healthCheck() {
    if (router.currentRoute.value.path === SERVER_ERROR_PAGE) {
      return;
    }
    healthService.ping(siteConfig?.healthCheckTimeout || DEFAULT_HEALTH_TIMEOUT_MS)
      .then(() => log.debug('Backend is up and running'))
      .catch(error => {
        log.warn('Backend is down', error);
        router.push(SERVER_ERROR_PAGE);
      });
  }

  onBeforeMount(healthCheck);
  onBeforeUpdate(healthCheck);

  const onResize = () => sendSize();
  const onLoad = () => {
    log.debug('window loaded, send size');
    sendSize();
  };
  window.addEventListener('resize', onResize);
  window.addEventListener('load', onLoad);
  onUnmounted(() => {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('load', onLoad);
  });

  onMounted(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.oldValue !== mutation.target.textContent) {
          sendSize();
        }
      });
    });
    const hfContentDOM = document.querySelector('.hf-content') || document.body;
    observer.observe(hfContentDOM, {
      characterDataOldValue: true,
      subtree: true,
      childList: true,
      characterData: true
    });
  });
</script>

<style lang="scss">

  @import 'assets/styles/style';

  .hf-content {
    min-height: 92vh;
  }

</style>
